import { Suspense, lazy, useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { useAnalytics } from './context/AnalyticsContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import SubscribePopup from './components/SubscribePopup';
import CookieConsent from './components/CookieConsent';
import AdminSidebar from './components/AdminSidebar';
import LoadingSpinner from './components/LoadingSpinner';

// Lazy load pages
const Home = lazy(() => import('./pages/Home'));
const Search = lazy(() => import('./pages/Search'));
const FacilityDetail = lazy(() => import('./pages/FacilityDetail'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const Favorites = lazy(() => import('./pages/Favorites'));
const Advertise = lazy(() => import('./pages/Advertise'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Terms = lazy(() => import('./pages/Terms'));
const Contact = lazy(() => import('./pages/Contact'));
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const AdminUsers = lazy(() => import('./pages/admin/Users'));
const AdminFacilities = lazy(() => import('./pages/admin/Facilities'));
const AdminSubscribers = lazy(() => import('./pages/admin/Subscribers'));
const AdminAnalytics = lazy(() => import('./pages/admin/Analytics'));
const AdminAds = lazy(() => import('./pages/admin/Ads'));
const AdminBlogManager = lazy(() => import('./pages/admin/BlogManager'));
const AdminWebSource = lazy(() => import('./pages/admin/WebSource'));
const AdminConversionSource = lazy(() => import('./pages/admin/ConversionSource'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPostPage = lazy(() => import('./pages/BlogPost'));
const Insurance = lazy(() => import('./pages/Insurance'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));
const HowItWorks = lazy(() => import('./pages/HowItWorks'));
const FAQ = lazy(() => import('./pages/FAQ'));
const About = lazy(() => import('./pages/About'));

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && user.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Admin Layout with Sidebar
const AdminLayout = ({ children }) => {
  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content">
        {children}
      </div>
    </div>
  );
};

function AppContent() {
  const location = useLocation();
  const { dismissPopup } = useAnalytics();
  const isHomePage = location.pathname === '/';
  const isAdminPage = location.pathname.startsWith('/admin');
  // Pages where footer should not show (full-screen map pages)
  const hideFooter = isHomePage || isAdminPage;

  // Subscribe popup — show after 30s delay on any page, once per session
  const [showSubscribe, setShowSubscribe] = useState(false);

  useEffect(() => {
    const hasSubscribed = localStorage.getItem('hasSubscribed');
    const hasDismissed = sessionStorage.getItem('popupDismissed');
    if (hasSubscribed || hasDismissed || isAdminPage) return;

    const timer = setTimeout(() => {
      setShowSubscribe(true);
    }, 30000);

    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCloseSubscribe = () => {
    setShowSubscribe(false);
    dismissPopup();
  };

  return (
    <div className="app">
      <Navbar transparent={isHomePage} />
      <Suspense fallback={<LoadingSpinner fullScreen />}>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/search" element={<Search />} />
          <Route path="/facility/:id" element={<FacilityDetail />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/advertise" element={<Advertise />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/insurance" element={<Insurance />} />
          <Route path="/cookies" element={<CookiePolicy />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/about" element={<About />} />

          {/* Protected Routes */}
          <Route
            path="/favorites"
            element={
              <ProtectedRoute>
                <Favorites />
              </ProtectedRoute>
            }
          />

          {/* Admin Routes */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute adminOnly>
                <AdminLayout>
                  <AdminDashboard />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute adminOnly>
                <AdminLayout>
                  <AdminUsers />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/facilities"
            element={
              <ProtectedRoute adminOnly>
                <AdminLayout>
                  <AdminFacilities />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/subscribers"
            element={
              <ProtectedRoute adminOnly>
                <AdminLayout>
                  <AdminSubscribers />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/analytics"
            element={
              <ProtectedRoute adminOnly>
                <AdminLayout>
                  <AdminAnalytics />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/web-source"
            element={
              <ProtectedRoute adminOnly>
                <AdminLayout>
                  <AdminWebSource />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/conversion-source"
            element={
              <ProtectedRoute adminOnly>
                <AdminLayout>
                  <AdminConversionSource />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/ads"
            element={
              <ProtectedRoute adminOnly>
                <AdminLayout>
                  <AdminAds />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/blog"
            element={
              <ProtectedRoute adminOnly>
                <AdminLayout>
                  <AdminBlogManager />
                </AdminLayout>
              </ProtectedRoute>
            }
          />

          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      {!hideFooter && <Footer />}
      <SubscribePopup isOpen={showSubscribe} onClose={handleCloseSubscribe} />
      <CookieConsent />
    </div>
  );
}

function App() {
  return <AppContent />;
}

export default App;
